import { Component, DestroyRef, ElementRef, inject, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { DatePipe, NgIf} from '@angular/common';
import { MatSortModule, Sort } from '@angular/material/sort';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { User } from '../../models/users.model';
import { HttpServiceService } from "../../services/http-service.service";
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { LoadingService } from '../../services/loading.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { Offcanvas } from 'bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'app-users-list',
  imports: [NgIf, ReactiveFormsModule, NgbModule,MatSortModule , PaginationComponent, DatePipe],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.css'
})
export class UsersListComponent implements OnDestroy {
  offcanvasExample: boolean = false;
  userForm! : FormGroup;
  orderData: User[] = [];
  allUsers : User[] = [];
  selectedUserId : string = '';
  private authService = inject(LocalStorageService);
  @ViewChild('addEditUser') offcanvas!: ElementRef;
  private offcanvasInstance: Offcanvas | null = null;

  constructor(private fb: FormBuilder, private modalService: NgbModal , private httpService : HttpServiceService, private alertService: NgxToastrService){
          
  }  

  @Input() offset_limit: any = 20;
  @Input() checkbox: boolean = false;

  active = 1;
  page: any = 1;
  totalRows: number = 5;
  totalPage: any = 0;
  allData: any = [];
  boxActive: Boolean = false;
  loadingService = inject(LoadingService);
  private getAllUsersSubscription! : Subscription;
  private editUsersSubscription? : Subscription;
  private getUserSubscription? : Subscription;
  private deleteUserSubscription? : Subscription;
  private addUserSubscription? : Subscription;
  searchForm!: FormGroup;
  disableClear: boolean = false;
  private destroyRef = inject(DestroyRef);
  
  ngOnInit(): void {
    this.userForm = this.fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      email : ['', [Validators.required,  Validators.email]],
      phone : ['', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')]],
      role_identifier : [null, [Validators.required]]
    });
    this.searchForm = this.fb.group({      
      search: [''],
      role_identifier: [null]
    });
    this.getAllUsers();
    this.searchForm.get('search')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.handleSearch(value);
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const offcanvasElement = this.offcanvas?.nativeElement;
      if (offcanvasElement) {
        this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
  }

  getAllUsers(query : any = ''){
    this.getAllUsersSubscription = this.httpService.getAllUsers(this.page, this.offset_limit, query).subscribe({
      next : (response) => {
        this.allUsers = response.data.users;
        this.orderData = this.allUsers.slice();
        this.totalRows = this.offset_limit;    
        this.allData = this.paginator(this.orderData, this.page, this.totalRows);
        this.totalPage = response.data.pageCount;
      },
      error : (error) => {
        console.log(error);
      }
    })
  }

  addUserToggle(){
    this.selectedUserId = '';
    this.toggleExample();
  }

  toggleExample() {    
    this.offcanvasExample = !this.offcanvasExample;
    if (this.offcanvasInstance) {
      if (this.offcanvasExample) {
        this.offcanvasInstance.show();
      } else {
        this.offcanvasInstance.hide();
      }
    }
  }

  openCenter(content: TemplateRef<any>) {
    this.modalService.open(content, { centered: true });
  }

  onSubmit(): void {
    if (this.userForm.valid) {
      let obj = {
        ...this.userForm.value,
        phone : this.userForm.get("phone")?.value.toString()
      }
      if(this.selectedUserId){
        this.editUsersSubscription = this.httpService.editUser(obj, this.selectedUserId).subscribe({
          next : (resp) => {
            this.toggleExample();
            this.alertService.success(resp.message, "toast-top-right");
            this.getAllUsers();            
          }
        })
      }else{
        this.addUserSubscription = this.httpService.addUser(obj).subscribe({
          next : (resp) => {
            this.toggleExample();
            this.userForm.reset();
            this.alertService.success(resp.message, "toast-top-right");
            this.getAllUsers();      
          }
        })
      }

    } else {
      console.log('Form is invalid.');
    }
  }

  selectUser(userId : string){
    this.selectedUserId = userId;    
    this.getUserSubscription = this.httpService.getUser(userId).subscribe({
      next : (userData) => {       
        this.userForm.patchValue(userData.data);
        this.userForm.patchValue({
          role_identifier : userData.data?.Role["role_identifier"]
        })
        this.toggleExample(); 
      },
      error : (error) => {
      }
    });    
  }

  deleteUser(userId : string){
    this.deleteUserSubscription = this.httpService.deleteUser(userId).subscribe({
      next : (resp) => {
        this.alertService.success(resp?.message, "toast-top-right");
        this.getAllUsers();
      }
    })
  }

  close(){
    this.toggleExample();    
    this.userForm.reset();
  }

  resetSearchForm() {
    this.page = 1;
    this.searchForm.reset({
      total_bedrooms: '',
      status: ''
    }, { emitEvent: false });
    this.getAllUsers();
    this.disableClear = false;
  }

  handleSearch(value: any, keepPage: string = '') {
    const searchData = this.searchForm.value;
    let query = '';
    for (let key in searchData) {
      if (searchData[key]) {
        query += `&${key}=${searchData[key]}`;
      }
    }
    if (!keepPage) this.page = 1;
    if (query || !value) {
      this.getAllUsers(query);
      this.disableClear = (value || query) ? true : false;
    }
  }
  

  sortData(sort: Sort) {
    const data = this.allUsers.slice();
    if (!sort.active || sort.direction === '') {
      this.orderData = data;
      return;
    }

    this.orderData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName': return compare(a.first_name, b.first_name, isAsc);
        case 'lastName': return compare(a.last_name, b.last_name, isAsc);
        case 'email': return compare(a.email, b.email, isAsc);
        case 'mobile': return compare(a.phone, b.phone, isAsc);
        case 'userType': return compare(a.role_identifier, b.role_identifier, isAsc);       
        case 'status': return compare(a.status ?? "", b.status ?? "", isAsc);
        default: return 0;
      }
    });
    this.allData = this.paginator(this.orderData, this.page, this.totalRows);
  }

  pageChange(e: any) {    //  Page Change funcation   ---------
    if(this.page == e) return;
    this.page = e;
    this.handleSearch('', 'keepPage');
  }

  paginator(items: any, current_page: any, per_page_items: any) {
    let page = current_page || 1,
      per_page = per_page_items || 10,
      offset = (page - 1) * per_page,

      paginatedItems = items,
      total_pages = Math.ceil(items.length / per_page);

    return {
      page: page,
      per_page: per_page,
      pre_page: page - 1 ? page - 1 : null,
      next_page: (total_pages > page) ? page + 1 : null,
      total: items.length,
      total_pages: total_pages,
      data: paginatedItems
    };
  }

  checkUncheckAll() {
    if (this.boxActive) {
      this.boxActive = false;
    } else {
      this.boxActive = true;
    }
  }

  get checkAddUser(){
      return this.authService.checkPermission(USER_PERMISSIONS.USERS.CREATE_USER);
  }

  get checkEditUser(){
    return this.authService.checkPermission(USER_PERMISSIONS.USERS.UPDATE_USER);
  }

  get checkDeleteUser(){
    return this.authService.checkPermission(USER_PERMISSIONS.USERS.DELETE_USER);
  }

  ngOnDestroy(){
    this.getAllUsersSubscription?.unsubscribe();
    this.editUsersSubscription?.unsubscribe();
    this.getUserSubscription?.unsubscribe();
    this.deleteUserSubscription?.unsubscribe();
    this.addUserSubscription?.unsubscribe();
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
